import { useEffect, useState } from 'react';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { toast } from '@/hooks/use-toast';
import { emailContactsApi, type EmailContact } from '../api';
import { Loader2, RefreshCw, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

const PAGE_SIZE = 20;

const SOURCE_LABEL: Record<string, string> = {
  USER_DATABASE: 'Sabuesos',
  FIREBASE_IMPORT: 'Firebase',
  CSV_IMPORT: 'CSV',
  MANUAL: 'Manual',
  OTHER: 'Otro',
};

function statusBadge(contact: EmailContact) {
  if (contact.isSuppressed) return <Badge variant="destructive">Suprimido</Badge>;
  if (contact.isUnsubscribed) return <Badge variant="secondary">Unsubscribed</Badge>;
  if (!contact.isEmailValid) return <Badge variant="outline">Inválido</Badge>;
  return <Badge variant="default">Activo</Badge>;
}

export default function EmailContactsAdminPage() {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [items, setItems] = useState<EmailContact[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const load = async () => {
    setLoading(true);
    try {
      const res = await emailContactsApi.list({
        page,
        pageSize: PAGE_SIZE,
        search: search || undefined,
        source: source !== 'all' ? source : undefined,
        isSuppressed: statusFilter === 'suppressed' ? true : undefined,
        isUnsubscribed: statusFilter === 'unsubscribed' ? true : undefined,
        status: statusFilter === 'invalid' ? 'invalid' : undefined,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.response?.data?.message || 'No se pudieron cargar los contactos.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, source, statusFilter]);

  const runSync = async () => {
    setSyncing(true);
    try {
      const result = await emailContactsApi.syncUsers();
      toast({
        title: 'Sincronización completada',
        description: `${result.created} nuevos, ${result.linkedExisting} vinculados, ${result.skippedInvalid} inválidos omitidos.`,
      });
      setPage(1);
      await load();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.response?.data?.message || 'No se pudo sincronizar.',
      });
    } finally {
      setSyncing(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Mail className="h-7 w-7" /> Contactos de Email
            </h1>
            <p className="text-muted-foreground">Base consolidada de destinatarios para campañas de email.</p>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/email-contacts/imports">
              <Button variant="outline">Importaciones</Button>
            </Link>
            <Button onClick={runSync} disabled={syncing}>
              {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Sincronizar usuarios
            </Button>
          </div>
        </div>

        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Input
              placeholder="Buscar por email o nombre..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setPage(1);
                  load();
                }
              }}
              className="max-w-xs"
            />
            <Select value={source} onValueChange={(v) => { setSource(v); setPage(1); }}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Origen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los orígenes</SelectItem>
                <SelectItem value="USER_DATABASE">Usuarios Sabuesos</SelectItem>
                <SelectItem value="FIREBASE_IMPORT">Firebase</SelectItem>
                <SelectItem value="CSV_IMPORT">Importado (CSV)</SelectItem>
                <SelectItem value="MANUAL">Manual</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="suppressed">Suprimidos</SelectItem>
                <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                <SelectItem value="invalid">Inválidos</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => { setPage(1); load(); }}>
              Filtrar
            </Button>
          </div>
        </Card>

        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead>Origen</TableHead>
                <TableHead>Usuario Sabuesos</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Fecha de inclusión</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Cargando...
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No se encontraron contactos.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((contact) => (
                  <TableRow key={contact.id}>
                    <TableCell>
                      <Link to={`/admin/email-contacts/${contact.id}`} className="hover:underline">
                        {contact.email}
                      </Link>
                    </TableCell>
                    <TableCell>{contact.name || '—'}</TableCell>
                    <TableCell>{SOURCE_LABEL[contact.createdSource] || contact.createdSource}</TableCell>
                    <TableCell>{contact.linkedUserId ? 'Sí' : 'No'}</TableCell>
                    <TableCell>{statusBadge(contact)}</TableCell>
                    <TableCell>{new Date(contact.createdAt).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>

        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {total} contacto{total === 1 ? '' : 's'} · página {page} de {totalPages}
          </p>
          <Pagination className="justify-end w-auto mx-0">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (page > 1) setPage(page - 1);
                  }}
                  className={page <= 1 ? 'pointer-events-none opacity-50' : ''}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (page < totalPages) setPage(page + 1);
                  }}
                  className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </div>
    </Layout>
  );
}
